import React, { useMemo, useState } from 'react'
import { keyBy, sumBy } from 'lodash'
import { useTeam } from 'app/frontend/base/config/context/team'
import {
  lootHistoryUrl,
  LootHistoryUrlOptions,
  updateTeamLootHistory,
} from 'app/frontend/base/api/team'
import { useTeamLootHistory } from 'app/frontend/base/config/queries'
import { Button, buttonVariants } from '@/components/ui/button'
import { DataTable } from '@/components/ui/data-table'
import { ColumnFilter } from '@/components/ui/column-filter'
import { ColumnDef, RowSelectionState } from '@tanstack/react-table'
import { Link } from 'react-router-dom'
import CharacterClassIcon from 'app/frontend/base/components/CharacterClassIcon'
import CharacterRoleIcon from 'app/frontend/base/components/CharacterRoleIcon'
import BlizzardIcon from 'app/frontend/base/components/BlizzardIcon'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  CircleQuestionMark,
  FileDown,
  LinkIcon,
  MessageCircleMore,
} from 'lucide-react'
import dayjs from 'dayjs'
import { useAuth } from 'app/frontend/base/config/context/auth'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { displayErrorToast } from 'app/frontend/base/helpers'
import { useTranslation } from 'react-i18next'
import { Skeleton } from '@/components/ui/skeleton'
import { genericSorting } from 'app/frontend/base/config/column-definitions/util'
import { itemSlotLabels } from 'app/frontend/types/custom/ItemSlot'
import { difficultiesLabels } from 'app/frontend/types/custom/Difficulty'

const LootHistoryTable = ({
  historyOptions,
  allDifficulties,
}: {
  historyOptions: LootHistoryUrlOptions
  allDifficulties: boolean
}) => {
  const { t } = useTranslation()
  const { team } = useTeam()
  const { accessLevel } = useAuth()
  const queryClient = useQueryClient()
  const [showIgnoredItems, setShowIgnoredItems] = useState(
    !localStorage.getItem('hidingIgnoredItems'),
  )
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})

  const { data, isPending } = useTeamLootHistory(historyOptions)

  const toggleIgnoreItems = () => {
    if (showIgnoredItems) {
      localStorage.setItem('hidingIgnoredItems', 'true')
      setShowIgnoredItems(false)
    } else {
      localStorage.removeItem('hidingIgnoredItems')
      setShowIgnoredItems(true)
    }
  }

  const membersById = useMemo(() => {
    return keyBy(
      (team.members ?? []).filter(
        (member) => member.teamRank?.wishlist_visibility,
      ),
      (member) => member.characterReference.id,
    )
  }, [team.members])

  const { mutate, isPending: isUpdating } = useMutation({
    mutationFn: async (status: 'discard' | 'restore') => {
      return await updateTeamLootHistory(
        team.id,
        Object.keys(rowSelection),
        status === 'discard',
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['teamLootHistory', historyOptions],
      })
      toast.success(t('shared.changes-saved'))
      setRowSelection({})
    },
    onError: (error) => {
      displayErrorToast(error, t)
    },
  })

  const filteredItems = useMemo(() => {
    return (
      data?.history_items.filter((item) => {
        return showIgnoredItems || !item.discarded
      }) ?? []
    )
  }, [data?.history_items, showIgnoredItems])

  const slotFilterOptions = useMemo(() => {
    const uniqueSlots = Array.from(
      new Set(filteredItems.map((item) => item.slot)),
    )
    return uniqueSlots
      .map((slot) => ({
        label: itemSlotLabels[slot] || slot,
        value: slot,
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [filteredItems])

  const responseFilterOptions = useMemo(() => {
    const uniqueResponses = Array.from(
      new Set(filteredItems.map((item) => item.response).filter(Boolean)),
    )
    return uniqueResponses
      .map((response) => ({
        label: response,
        value: response,
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [filteredItems])

  const difficultyFilterOptions = useMemo(() => {
    const uniqueDifficulties = Array.from(
      new Set(filteredItems.map((item) => item.difficulty)),
    )
    return uniqueDifficulties
      .map((difficulty) => ({
        label: difficultiesLabels[difficulty],
        value: difficulty,
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [filteredItems])

  const WrappedCell = ({
    children,
    className,
    item,
  }: {
    children: React.ReactNode
    className?: string
    item: wowaudit.LootHistoryItem
  }) => {
    return (
      <div
        className={`flex items-center whitespace-nowrap ${className} ${
          item.discarded ? 'opacity-50 line-through' : ''
        }`}
      >
        {children}
      </div>
    )
  }

  const columns: ColumnDef<wowaudit.LootHistoryItem>[] = useMemo(() => {
    return [
      {
        ...genericSorting('text', 'Character'),
        accessorFn: (row) => {
          return membersById[row.character_id]?.characterReference.name ?? ''
        },
        id: 'name',
        size: 175,
        cell: (info) => {
          const member = membersById[info.row.original.character_id]

          return (
            <WrappedCell item={info.row.original} className='flex'>
              <Link
                to={member.characterReference.path}
                className='inline-flex gap-2 items-center'
              >
                <CharacterRoleIcon role={member.role} />
                <CharacterClassIcon
                  characterClass={member.characterReference.characterClass}
                />

                {member.characterReference.name}
              </Link>
            </WrappedCell>
          )
        },
      },
      {
        id: 'item',
        accessorKey: 'name',
        ...genericSorting('text', 'New item'),
        cell: (info) => {
          return (
            <WrappedCell item={info.row.original}>
              <a
                href={`https://wowhead.com/item=${
                  info.row.original.encounter_item_id
                }&bonus=${info.row.original.bonus_ids.join(':')}`}
                target='_blank'
                rel='noreferrer'
                className={`text-quality-${info.row.original.quality} inline-flex items-center gap-2`}
              >
                <BlizzardIcon size='medium' iconName={info.row.original.icon} />
                {info.row.original.name}
              </a>
            </WrappedCell>
          )
        },
      },
      {
        accessorKey: 'difficulty',
        header: ({ column }) => (
          <div className='flex items-center space-x-2'>
            <span
              className='cursor-pointer'
              onClick={() => column.toggleSorting()}
            >
              Difficulty
            </span>
            <ColumnFilter column={column} options={difficultyFilterOptions} />
          </div>
        ),
        cell: (info) => {
          return (
            <WrappedCell item={info.row.original}>
              {difficultiesLabels[info.row.original.difficulty]}
            </WrappedCell>
          )
        },
        sortingFn: 'text',
      },
      {
        header: ({ column }) => (
          <div className='flex items-center space-x-2'>
            <span
              className='cursor-pointer'
              onClick={() => column.toggleSorting()}
            >
              Slot
            </span>
            <ColumnFilter column={column} options={slotFilterOptions} />
          </div>
        ),
        sortingFn: 'text',
        accessorKey: 'slot',
        filterFn: (row, id, value) => {
          if (!value || value.length === 0) return true
          return value.includes(row.getValue(id))
        },
        cell: (info) => {
          return (
            <WrappedCell item={info.row.original}>
              {itemSlotLabels[info.row.original.slot]}
            </WrappedCell>
          )
        },
      },
      {
        header: ({ column }) => (
          <div className='flex items-center space-x-2'>
            <span
              className='cursor-pointer'
              onClick={() => column.toggleSorting()}
            >
              Response
            </span>
            <ColumnFilter column={column} options={responseFilterOptions} />
          </div>
        ),
        sortingFn: 'text',
        accessorKey: 'response',
        filterFn: (row, id, value) => {
          if (!value || value.length === 0) return true
          return value.includes(row.getValue(id))
        },
        cell: (info) => {
          return (
            <WrappedCell
              item={info.row.original}
              className='inline-flex items-center gap-1 max-w-40'
            >
              {info.row.original.note && (
                <Tooltip>
                  <TooltipTrigger>
                    <MessageCircleMore size={16} />
                  </TooltipTrigger>
                  <TooltipContent className='max-w-[50vw] whitespace-normal'>
                    {info.row.original.note}
                  </TooltipContent>
                </Tooltip>
              )}
              <span className='truncate'>{info.row.original.response}</span>
            </WrappedCell>
          )
        },
      },
      {
        header: 'Old',
        accessorKey: 'old_items',
        cell: (info) => {
          return (
            <WrappedCell
              item={info.row.original}
              className='inline-flex items-center gap-1'
            >
              {info.row.original.old_items.map((item, i) => (
                <a
                  key={i}
                  href={`https://wowhead.com/item=${
                    item.item_id
                  }&bonus=${item.bonus_ids.join(':')}`}
                  target='_blank'
                  rel='noreferrer'
                >
                  <LinkIcon size={14} />
                </a>
              ))}
            </WrappedCell>
          )
        },
      },
      {
        header: ({ column }) => (
          <Tooltip>
            <TooltipTrigger className='mt-1'>
              <CircleQuestionMark
                size={15}
                onClick={() => column.toggleSorting()}
              />
            </TooltipTrigger>
            <TooltipContent side='left' className='max-w-60 font-normal'>
              <p className='mb-2'>
                Displays the amount of other characters that had the same
                response.
              </p>
              <div className='text-muted-foreground'>
                This is only available for items that are awarded when the
                wowaudit addon is enabled.
              </div>
            </TooltipContent>
          </Tooltip>
        ),
        sortingFn: 'alphanumeric',
        accessorKey: 'same_response_amount',
      },
      {
        header: ({ column }) => (
          <span className='inline-flex items-center gap-1 whitespace-nowrap'>
            <span
              className='cursor-pointer'
              onClick={() => column.toggleSorting()}
            >
              Wishes
            </span>
            <Tooltip>
              <TooltipTrigger>
                <CircleQuestionMark size={15} />
              </TooltipTrigger>
              <TooltipContent
                side='left'
                className='max-w-60 font-normal whitespace-normal'
              >
                <p className='mb-2'>
                  Displays the wishes that were shown in-game during the loot
                  session.
                </p>
                <div className='text-muted-foreground'>
                  This is only available for items that are awarded when the
                  wowaudit addon is enabled.
                </div>
              </TooltipContent>
            </Tooltip>
          </span>
        ),
        accessorFn: (row) => {
          return sumBy(row.wish_data, (wish) => wish.value ?? 0)
        },
        id: 'wish_data',
        sortingFn: 'alphanumeric',
        cell: (info) => {
          return info.row.original.wish_data.map((wish, i) => (
            <WrappedCell
              item={info.row.original}
              key={i}
              className='flex items-center gap-1.5'
            >
              <Tooltip>
                <TooltipTrigger>
                  <BlizzardIcon size='small' iconName={wish.spec_icon} />
                </TooltipTrigger>
                <TooltipContent>{wish.spec_name}</TooltipContent>
              </Tooltip>
              {wish.value}
            </WrappedCell>
          ))
        },
      },
      {
        ...genericSorting('text', 'Awarded by'),
        accessorFn: (row) => {
          return (
            membersById[row.awarded_by_id ?? 0]?.characterReference.name ??
            row.awarded_by_name ??
            ''
          )
        },
        id: 'awarded_by',
        cell: (info) => {
          const characterReference =
            membersById[info.row.original.awarded_by_id ?? 0]
              ?.characterReference

          if (characterReference) {
            return (
              <WrappedCell item={info.row.original}>
                <Link
                  to={characterReference.path}
                  className='inline-flex items-center gap-1.5'
                >
                  <CharacterClassIcon
                    characterClass={characterReference.characterClass}
                    size='small'
                  />
                  <span>{characterReference.name}</span>
                </Link>
              </WrappedCell>
            )
          } else if (info.row.original.awarded_by_name) {
            const [name, realm] = info.row.original.awarded_by_name.split('-')
            return (
              <WrappedCell item={info.row.original}>
                <Link
                  to={`/character/${team.ownerReference.realm.region.toLowerCase()}/${realm.toLowerCase()}/${name.toLowerCase()}`}
                  className='inline-flex items-center gap-1.5'
                >
                  <img
                    src='https://data.wowaudit.com/img/Unknown-2.png'
                    className='w-6'
                  />
                  <span>{name}</span>
                </Link>
              </WrappedCell>
            )
          }
        },
      },
      {
        accessorKey: 'awarded_at',
        ...genericSorting(
          (rowA, rowB) => {
            return dayjs(rowA.original.awarded_at).diff(
              dayjs(rowB.original.awarded_at),
            )
          },
          'Date',
          true,
        ),
        cell: (info) => {
          return (
            <WrappedCell item={info.row.original}>
              {dayjs(info.row.original.awarded_at).format('MMM D')}
            </WrappedCell>
          )
        },
      },
    ]
  }, [
    membersById,
    filteredItems,
    slotFilterOptions,
    responseFilterOptions,
    allDifficulties,
  ])

  return (
    <div>
      <div className='flex items-center justify-end flex-wrap gap-4 mb-2'>
        <h3 className='flex items-center gap-3 mb-0 grow'>
          Items{' '}
          <a
            href={lootHistoryUrl({ ...historyOptions, type: 'xlsx' })}
            download='loot_history.xlsx'
            className={`flex items-center gap-1.5 bg-card ${buttonVariants({
              variant: 'outline',
              size: 'sm',
            })}`}
          >
            <FileDown size={16} /> Export
          </a>
        </h3>

        <div
          className={`flex items-center gap-1 ${
            Object.keys(rowSelection).length === 0
              ? 'opacity-0'
              : 'opacity-100 transition-opacity'
          }`}
        >
          <span>
            {Object.keys(rowSelection).length} item
            {Object.keys(rowSelection).length !== 1 ? 's' : ''} selected,
          </span>
          <Button
            size='xs'
            variant='outline'
            loading={isUpdating}
            onClick={() => mutate('discard')}
          >
            ignore
          </Button>
          <span>or</span>
          <Button
            size='xs'
            variant='outline'
            loading={isUpdating}
            onClick={() => mutate('restore')}
          >
            enable
          </Button>
          for statistics
        </div>

        <Button
          variant='outline'
          size='xs'
          onClick={toggleIgnoreItems}
          className='h-8 text-sm'
        >
          {showIgnoredItems ? 'Hide' : 'Show'} ignored items
        </Button>
      </div>

      {isPending ? (
        <Skeleton className='h-160' />
      ) : (
        <DataTable
          columns={columns}
          data={filteredItems}
          filterType={(row, _columnId, value) => {
            const searchableColumns = ['name', 'item']

            if (!value) return true

            const searchValue = value.toLowerCase()

            return searchableColumns.some((colId) => {
              const cellValue = row.getValue(colId)
              return cellValue?.toString().toLowerCase().includes(searchValue)
            })
          }}
          pagination={25}
          includeHeader={true}
          sortingState={[{ id: 'awarded_at', desc: true }]}
          rowSelection={accessLevel === 'admin' ? rowSelection : undefined}
          onRowSelectionChange={
            accessLevel === 'admin' ? setRowSelection : undefined
          }
          getRowId={(row) => row.id.toString()}
          visibilityState={{
            difficulty: allDifficulties,
          }}
        />
      )}
    </div>
  )
}

export default LootHistoryTable
